import {
  ButtonV2,
  LabraToolTipV2,
  ModalConfirmV2,
  ModalSizeVariants,
  PlanType,
  StatusBar,
  StatusBarColors,
  StatusBarDividerColors,
} from '@labrav/react-components'
import {
  OfferStatus,
  OfferTypeRenew,
} from '../../../../modules/privateOffer/reducer'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faClock,
  faHourglassStart,
  faPen,
} from '@fortawesome/pro-regular-svg-icons'
import { ExtendOfferExpirationDateButton } from '../../../ExtendOfferExpirationDateModal'
import { productDetailDate } from '../../../../common/utils/productCreationUtil'
import { useStyles } from './ChannelOfferHeader.styles'
import { getStatusAndLabelForPrivateOffer } from '../../../../common/utils/getStatusAndLabelForPrivateOffer'
import {
  getExpirationFromDate,
  getFormattedLabraDate,
} from '../../../../common/utils/dates'
import { useState, useMemo } from 'react'
import { useAuth } from '@labrav/react-components'
import { useNavigate, useParams } from 'react-router-dom'
import { Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useDispatch, useSelector } from '../../../../store'
import { updateChannelPartnerPrivateOffer } from '../../../../modules/channelPartnerPrivateOffer/action'
import { ChannelOfferHeaderActions } from './ChannelOfferHeaderActions/ChannelOfferHeaderActions'
import { allowedPlanTypesForUpdateExpiryDate } from '../../../../utils/constants'
import { isOfferExpired } from '../../../../common/utils/isOfferExpired'
import { useAccountId } from '@labrav/common'
import type { CloudMarketplace } from '../../../../modules/productsListing/types'
import { providerToAuthCloud } from '@labrav/common'
import {
  LoadingTypes,
  isLoading as Loading,
  useCurrentCloudProvider,
} from '@labrav/common'
import type { ChannelOfferHeaderProps } from './types'

export type { ChannelOfferHeaderProps } from './types'

export const ChannelOfferHeader = ({
  channelPrivateOffer,
  planType,
  crmOpportunityId,
  resaleAuthorizationId,
}: ChannelOfferHeaderProps) => {
  const { classes } = useStyles()
  const theme = useTheme()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { cloud: routeCloud, accountId: routeAccountId } = useParams<{
    cloud?: string
    accountId?: string
  }>()
  const accountIdFromHook = useAccountId({ product: 'flyout' })
  const currentCloudProvider = useCurrentCloudProvider()
  const selectedCloud = routeCloud || currentCloudProvider

  const { loading: authLoading, canDo } = useAuth()
  const canWriteMarketplace = useMemo(
    () =>
      !authLoading &&
      canDo({
        cloud: providerToAuthCloud(selectedCloud.toLowerCase()),
        service: 'marketplace',
        action: 'create',
      }),
    [authLoading, canDo, selectedCloud]
  )
  const isReadOnly = !canWriteMarketplace
  const permissionMessageString =
    'This action is disabled because you have read-only access'

  const accountId = routeAccountId || accountIdFromHook

  const { id, offerStatus, error, updatedAt, offerExpirationDate } =
    channelPrivateOffer

  const { fullDateTime } = updatedAt
    ? productDetailDate(updatedAt)
    : { fullDateTime: '' }

  const { fullDateTime: fullCancelledAt } = channelPrivateOffer?.auditJson
    ?.offerCancelledAt
    ? productDetailDate(channelPrivateOffer?.auditJson?.offerCancelledAt)
    : { fullDateTime: '' }

  const {
    modifiedDate: offerExpirationDateDisplay,
    modifiedTime: offerExpirationDateTime,
  } = offerExpirationDate
    ? productDetailDate(offerExpirationDate)
    : { modifiedDate: '', modifiedTime: '' }

  const formattedAcceptedDate = channelPrivateOffer.auditJson?.offerAcceptedAt
    ? getFormattedLabraDate(channelPrivateOffer.auditJson?.offerAcceptedAt)
        .formattedDate
    : '-'
  const formattedAcceptedTime = channelPrivateOffer.auditJson?.offerAcceptedAt
    ? getFormattedLabraDate(channelPrivateOffer.auditJson?.offerAcceptedAt)
        .formattedTime
    : '-'

  const [openEditOfferModal, setOpenEditOfferModal] = useState(false)

  const fullDateExpirationTimeForHeader = `${offerExpirationDateDisplay} ${offerExpirationDateTime}`

  const { isExpriringSoon, dayDiff } = getExpirationFromDate(
    offerExpirationDate || '',
    5
  )
  const offerExpiringSoon =
    isExpriringSoon &&
    ![
      OfferStatus.PRIVATE_OFFER_ACCEPTED,
      OfferStatus.PRIVATE_OFFER_EXPIRED,
      OfferStatus.PRIVATE_OFFER_RESTRCITED,
      OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS,
      OfferStatus.PRIVATE_OFFER_CREATION_IN_PROGRESS,
      OfferStatus.PRIVATE_OFFER_FAILED,
    ].includes(offerStatus || OfferStatus.PRIVATE_OFFER_CREATION_IN_PROGRESS)

  const statusLabel = getStatusAndLabelForPrivateOffer(
    offerStatus || OfferStatus.PRIVATE_OFFER_CREATION_IN_PROGRESS
  )

  const isLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.GENERAL)
  )

  const handleClickEditFailedPO = () => {
    setOpenEditOfferModal(true)
  }
  const handleEditOfferCloseModal = () => {
    setOpenEditOfferModal(false)
  }
  const handleEditOfferConfirm = async () => {
    await dispatch(
      updateChannelPartnerPrivateOffer(
        id,
        {},
        undefined,
        true,
        handleEditOfferCloseModal,
        resaleAuthorizationId,
        accountId,
        selectedCloud as CloudMarketplace
      )
    )

    const cloudPath = routeCloud?.toLowerCase() || selectedCloud.toLowerCase()

    const channelOfferEditPath =
      routeCloud && accountId
        ? `/flyout/${cloudPath}/${accountId}/authorization/${channelPrivateOffer.resaleAuthorizationId}/channel-offers/${id}/edit`
        : `/flyout/authorization/${channelPrivateOffer.resaleAuthorizationId}/channel-offers/${id}/edit`

    navigate(channelOfferEditPath)
  }
  const handleViewSubscription = () => {
    const cloudPath = routeCloud?.toLowerCase() || selectedCloud.toLowerCase()

    const subscriptionDetailPath =
      routeCloud && accountId
        ? `/flyout/${cloudPath}/${accountId}/subscriptions/${channelPrivateOffer?.labraSubscriptionId}/detail`
        : `/flyout/subscriptions/${channelPrivateOffer?.labraSubscriptionId}/detail`

    navigate(subscriptionDetailPath)
  }

  const isExpired = isOfferExpired(
    new Date(channelPrivateOffer?.offerExpirationDate || '')
  )

  const updateOfferStatusCheck =
    (offerStatus === OfferStatus?.PRIVATE_OFFER_CREATION_IN_PROGRESS &&
      !channelPrivateOffer.draft) ||
    offerStatus === OfferStatus?.PRIVATE_OFFER_FAILED ||
    offerStatus === OfferStatus.PRIVATE_OFFER_RESTRCITED ||
    offerStatus === OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS ||
    offerStatus === OfferStatus.PRIVATE_OFFER_ACCEPTED ||
    (isExpired &&
      planType &&
      !allowedPlanTypesForUpdateExpiryDate.includes(planType))

  const extendExpiryButton = (
    <ExtendOfferExpirationDateButton
      offerExpirationDate={channelPrivateOffer?.offerExpirationDate || ''}
      offerId={channelPrivateOffer.id}
      planType={channelPrivateOffer?.productType as PlanType}
      offerSubscriptionEndDate={channelPrivateOffer?.subscriptionEndDate}
      offerStatus={channelPrivateOffer?.offerStatus}
      isDraft={channelPrivateOffer?.draft}
      agreementStartDate={channelPrivateOffer?.agreementStartDate}
      type={channelPrivateOffer?.type as OfferTypeRenew}
      extendOfferType="CPPO"
      updateOfferStatusCheck={updateOfferStatusCheck || false}
      updateOfferExpiringToolTipMessage={''}
      classes={{
        updateButtonContainer: classes.updateButtonContainer,
        disabledEdit: classes.disabledEdit,
        extendOfferExpiry: classes.extendOfferExpiry,
      }}
    ></ExtendOfferExpirationDateButton>
  )

  return (
    <div className={classes.statusWrapper} data-testid={'offer-header'}>
      {offerStatus === OfferStatus.PRIVATE_OFFER_FAILED && (
        <StatusBar
          status={statusLabel.label}
          dividerColor={StatusBarDividerColors.SECONDARY}
          statusBarChipType="secondary"
          backgroundColor={StatusBarColors.RED}
          statusType={'error'}
          content={
            'Private offer creation has failed on AWS. Please edit the form below to address the issues.'
          }
          subContent={
            <div
              className={classes.errorSubContent}
              data-testid={'failed-offer-content'}
            >
              <div className={classes.errorMsg}>Issues found: {error}</div>
              <div className={classes.offerUpdatedAt}>{fullDateTime}</div>
              <LabraToolTipV2
                tooltipPlacement="bottom"
                description={
                  !canWriteMarketplace ? permissionMessageString : undefined
                }
              >
                <span>
                  <ButtonV2
                    className={classes.editButton}
                    styleType="link"
                    data-testid={'failed-offer-edit'}
                    onClick={handleClickEditFailedPO}
                    disabled={!canWriteMarketplace}
                  >
                    <FontAwesomeIcon
                      icon={faPen}
                      style={{
                        width: '16px',
                        height: '16px',
                        fontSize: '16px',
                        padding: '0px 12px 0px 0px',
                        color: theme.palette.primary.main,
                      }}
                    />
                    Edit offer
                  </ButtonV2>
                </span>
              </LabraToolTipV2>
              <ModalConfirmV2
                icon={
                  <FontAwesomeIcon
                    icon={faPen}
                    style={{ width: '16px', height: '16px' }}
                  />
                }
                title={'Edit this Offer ?'}
                description={
                  <Typography className={classes.modalText}>
                    <p>
                      Clicking this will change the offer to draft mode.
                      <br />
                      Are you sure you want to edit this offer ?
                    </p>
                  </Typography>
                }
                open={openEditOfferModal}
                titleAcceptButton={'Edit'}
                titleCancelButton={'Cancel'}
                onAccept={handleEditOfferConfirm}
                onDecline={handleEditOfferCloseModal}
                variant={ModalSizeVariants.small}
                acceptLoading={isLoading}
              />
            </div>
          }
        />
      )}
      {offerStatus === OfferStatus.PRIVATE_OFFER_EXPIRED && (
        <StatusBar
          status={statusLabel.label}
          dividerColor={StatusBarDividerColors.SECONDARY}
          statusBarChipType="secondary"
          backgroundColor={StatusBarColors.RED}
          statusType={'error'}
          content={
            <div
              className={classes.expired}
              data-testid={'expired-offer-content'}
            >
              <div>Offer expired on {offerExpirationDateDisplay}.</div>
              {extendExpiryButton}
            </div>
          }
        />
      )}
      {offerStatus === OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS && (
        <StatusBar
          backgroundColor={StatusBarColors.RED}
          dividerColor={StatusBarDividerColors.SECONDARY}
          content={
            <div
              className={classes.cancellationInprogress}
              data-testid={'restriction-inprogress-offer-content'}
            >
              <FontAwesomeIcon
                icon={faHourglassStart}
                style={{
                  width: '16px',
                  height: '16px',
                  fontSize: '16px',
                  padding: '0px 12px 0px 0px',
                  color: theme.palette.text.primary,
                }}
              />
              <div>
                The offer cancellation is in progress and will be done in
                approximately 5 minutes.
              </div>
            </div>
          }
        />
      )}
      {offerStatus === OfferStatus.PRIVATE_OFFER_RESTRCITED && (
        <StatusBar
          backgroundColor={StatusBarColors.RED}
          dividerColor={StatusBarDividerColors.SECONDARY}
          statusType={'error'}
          status={statusLabel.label}
          statusBarChipType="secondary"
          content={`Offer has been cancelled on ${fullCancelledAt}.`}
        />
      )}
      {offerStatus === OfferStatus.PRIVATE_OFFER_ACCEPTED && (
        <div className={classes.offerAcceptedWrapper}>
          <StatusBar
            status={statusLabel.label}
            dividerColor={StatusBarDividerColors.SECONDARY}
            content={
              <>
                <div
                  className={classes.offerAccepted}
                  data-testid={'accepted-offer-content'}
                >
                  <div>
                    Offer was accepted on {formattedAcceptedDate} |{' '}
                    {formattedAcceptedTime}
                  </div>
                  {
                    <ButtonV2
                      styleType="outlined"
                      className={classes.viewSubscription}
                      onClick={handleViewSubscription}
                    >
                      View contract
                    </ButtonV2>
                  }
                </div>
              </>
            }
            statusBarChipType="secondary"
            backgroundColor={StatusBarColors.GREEN}
            statusType="success"
          />
        </div>
      )}
      {offerStatus === OfferStatus.PRIVATE_OFFER_CREATION_IN_PROGRESS && (
        <StatusBar
          status={statusLabel.label}
          statusType={'infoSecondary'}
          dividerColor={StatusBarDividerColors.SECONDARY}
          statusBarChipType="secondary"
          content={
            <div data-testid={'inprogress-offer-content'}>
              The offer is being created in the AWS Marketplace and will be
              ready in approximately 5 minutes. Please refresh the page
              periodically to check the status.
            </div>
          }
        />
      )}
      {offerExpiringSoon && (
        <StatusBar
          backgroundColor={StatusBarColors.YELLOW}
          content={
            <div
              className={classes.expired}
              data-testid={'expiring-soon-offer-content'}
            >
              <div className={classes.offerExpiringSoon}>
                <FontAwesomeIcon
                  icon={faClock}
                  style={{
                    width: '16px',
                    height: '16px',
                    fontSize: '16px',
                    color: theme.palette.text.primary,
                  }}
                />
                <div>
                  {dayDiff === 1
                    ? 'Offer expires today.'
                    : `Offer expiring in ${dayDiff} days.
                  Expires at ${fullDateExpirationTimeForHeader}.`}
                </div>
              </div>
              {extendExpiryButton}
            </div>
          }
        />
      )}
      <ChannelOfferHeaderActions
        channelPrivateOffer={channelPrivateOffer}
        crmOpportunityId={crmOpportunityId}
        resaleAuthorizationId={resaleAuthorizationId}
      />
    </div>
  )
}
